import { PageContent } from 'components/layout/PageContent'
import { PageLoading } from 'components/loading'
import { useIframesAtendimentoQuery } from 'graphql/hooks.generated'
import { TipoAtendimentoProfissional, TipoSistemaExternoEnum } from 'graphql/types.generated'
import { useEffect, useMemo } from 'react'
import { Redirect, useHistory, useParams, useRouteMatch } from 'react-router-dom'

import { AtendimentoFooter } from '../AtendimentoFooter'
import type { AtendimentoIframe } from './SistemasExternosAtendimentoContext'
import { isMesmoSistemaExterno, useSistemasExternosAtendimento } from './SistemasExternosAtendimentoContext'
import { SistemasExternosAtendimentoList } from './SistemasExternosAtendimentoList'

interface SistemasExternosAtendimentoViewProps {
  atendimentoId?: ID
  cidadaoId: ID
  tipoAtendimentoProfissional?: TipoAtendimentoProfissional
  isRegistroTardio?: boolean
  clearCache?(updateState?: boolean): void
  isAtendimentoObservacao?: boolean
  isObservacaoAndResponsavel?: boolean
  hasFooter?: boolean
}

export function SistemasExternosAtendimentoView(props: Readonly<SistemasExternosAtendimentoViewProps>) {
  const {
    atendimentoId,
    cidadaoId,
    tipoAtendimentoProfissional,
    isRegistroTardio = false,
    clearCache,
    isAtendimentoObservacao = false,
    isObservacaoAndResponsavel = false,
    hasFooter = true,
  } = props

  const { data, loading: loadingIframes } = useIframesAtendimentoQuery({
    variables: {
      input: {
        cidadaoId,
        tipoAtendimentoProfissional,
        tipoSistemaExterno: TipoSistemaExternoEnum.ATENDIMENTO,
      },
    },
  })

  const iframesAtendimento = useMemo<AtendimentoIframe[]>(
    () => data?.iframes?.filter((iframe): iframe is AtendimentoIframe => !!iframe) ?? [],
    [data?.iframes]
  )

  const { iframeId } = useParams<{ iframeId?: string }>()
  const match = useRouteMatch()
  const history = useHistory()
  const { sistemaExternoAtivo, registrarSistemaExternoAtivo } = useSistemasExternosAtendimento()

  const iframeSelecionado = useMemo<AtendimentoIframe | null>(() => {
    if (!iframeId || !iframesAtendimento) return null
    return iframesAtendimento.find((iframe) => iframe?.id === iframeId) ?? null
  }, [iframeId, iframesAtendimento])

  useEffect(() => {
    if (iframeSelecionado) {
      registrarSistemaExternoAtivo(iframeSelecionado, cidadaoId)
    }
  }, [cidadaoId, iframeSelecionado, registrarSistemaExternoAtivo])

  const isSistemaExternoAtivoMesmoCidadao = !!sistemaExternoAtivo && sistemaExternoAtivo.cidadaoId === cidadaoId

  const isIframeAtivoRenderizado =
    isSistemaExternoAtivoMesmoCidadao &&
    (iframeSelecionado
      ? isMesmoSistemaExterno(sistemaExternoAtivo, iframeSelecionado)
      : sistemaExternoAtivo.id === iframeId)

  const shouldRestoreSistemaExternoAtivo =
    !iframeId &&
    history.action !== 'POP' &&
    isSistemaExternoAtivoMesmoCidadao &&
    (loadingIframes || iframesAtendimento.some((iframe) => isMesmoSistemaExterno(iframe, sistemaExternoAtivo)))

  const renderContent = () => {
    if (shouldRestoreSistemaExternoAtivo) {
      return <Redirect to={`${match.url}/${sistemaExternoAtivo.id}`} />
    }

    if (iframeId) {
      if (isIframeAtivoRenderizado) return null

      if (iframeSelecionado || loadingIframes) {
        return (
          <PageContent fluid type='filled'>
            <PageLoading message='Carregando sistema externo...' />
          </PageContent>
        )
      }
    }

    return (
      <PageContent fluid type='filled'>
        <SistemasExternosAtendimentoList items={iframesAtendimento} />
      </PageContent>
    )
  }

  return (
    <>
      {renderContent()}

      {hasFooter && clearCache && (
        <AtendimentoFooter
          atendimentoId={atendimentoId}
          tipoAtendimentoProfissional={tipoAtendimentoProfissional}
          clearCache={clearCache}
          isRegistroTardio={isRegistroTardio}
          isAtendimentoObservacao={isAtendimentoObservacao}
          isObservacaoAndResponsavel={isObservacaoAndResponsavel}
        />
      )}
    </>
  )
}
